import { useState, useEffect } from "react";
import {
  RefreshCw, Plus, Edit2, Trash2, X, Users,
  Calendar, DollarSign, Package, ToggleLeft, ToggleRight,
} from "lucide-react";
import { useAdmin } from "../admin-context";

const CYCLE_LABEL = { weekly: "Weekly", biweekly: "Bi-weekly", monthly: "Monthly" };
const STATUS_CHIP = {
  active:    "bg-green-100 text-green-800",
  paused:    "bg-yellow-100 text-yellow-800",
  cancelled: "bg-red-100 text-red-700",
};

const Drawer = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

const inp = "w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300";
const sel = `${inp} bg-white`;

const EMPTY_PLAN = { name: "", description: "", price: "", billing_cycle: "weekly", meals_per_cycle: 5, status: "active" };

const SubscriptionsAdminPage = () => {
  const { subscriptionPlans, loadSubscriptionPlans, createSubscriptionPlan, updateSubscriptionPlan,
    deleteSubscriptionPlan, subscriptions, loadSubscriptions, updateSubscriptionStatus, showToast } = useAdmin();
  const [tab, setTab] = useState("plans"); // plans | subscribers
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PLAN);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSubscriptionPlans(); loadSubscriptions(); }, []);

  const F = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openAdd  = () => { setEditing(null); setForm(EMPTY_PLAN); setDrawerOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setDrawerOpen(true); };

  const handleSave = async () => {
    if (!form.name || !form.price) return showToast("Name and price are required", "error");
    setSaving(true);
    if (editing) await updateSubscriptionPlan(editing.id, form);
    else         await createSubscriptionPlan(form);
    setSaving(false);
    setDrawerOpen(false);
    showToast(editing ? "Plan updated" : "Plan created");
  };

  const handleDeletePlan = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    await deleteSubscriptionPlan(id);
    showToast("Plan deleted");
  };

  const handleTogglePlan = async (plan) => {
    await updateSubscriptionPlan(plan.id, { status: plan.status === "active" ? "inactive" : "active" });
    showToast("Plan updated");
  };

  const handleSubStatus = async (id, status) => {
    await updateSubscriptionStatus(id, status);
    showToast("Subscription updated");
  };

  const plans = subscriptionPlans || [];
  const subs  = subscriptions    || [];
  const activeSubs = subs.filter((s) => s.status === "active").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Subscriptions</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage meal plans and recurring orders</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { loadSubscriptionPlans(); loadSubscriptions(); }}
            className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            <RefreshCw size={15} /> Refresh
          </button>
          {tab === "plans" && (
            <button onClick={openAdd} className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600">
              <Plus size={16} /> New Plan
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Package size={20} className="text-blue-600" /></div>
          <div><p className="text-xs text-gray-500">Active Plans</p><p className="text-2xl font-bold text-gray-900">{plans.filter((p) => p.status === "active").length}</p></div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><Users size={20} className="text-green-600" /></div>
          <div><p className="text-xs text-gray-500">Active Subscribers</p><p className="text-2xl font-bold text-green-700">{activeSubs}</p></div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center"><DollarSign size={20} className="text-orange-500" /></div>
          <div><p className="text-xs text-gray-500">Total Subscribers</p><p className="text-2xl font-bold text-gray-900">{subs.length}</p></div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {[["plans","Plans"], ["subscribers","Subscribers"]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition ${tab === key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Plans tab */}
      {tab === "plans" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.length === 0 ? (
            <div className="col-span-3 bg-white rounded-xl border p-12 text-center text-gray-400">
              <Package size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">No plans yet. Create your first meal plan.</p>
            </div>
          ) : plans.map((plan) => (
            <div key={plan.id} className={`bg-white rounded-xl border p-5 flex flex-col gap-3 ${plan.status === "inactive" ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-gray-900">{plan.name}</h3>
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${plan.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {plan.status}
                  </span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(plan)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><Edit2 size={14} /></button>
                  <button onClick={() => handleDeletePlan(plan.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
              {plan.description && <p className="text-xs text-gray-500">{plan.description}</p>}
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-3xl font-extrabold text-orange-500">${Number(plan.price).toFixed(2)}</p>
                  <p className="text-xs text-gray-400">{CYCLE_LABEL[plan.billing_cycle] || plan.billing_cycle}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-700">{plan.meals_per_cycle}</p>
                  <p className="text-xs text-gray-400">meals/cycle</p>
                </div>
              </div>
              <button onClick={() => handleTogglePlan(plan)}
                className="flex items-center gap-2 text-xs font-medium py-1.5 px-3 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition">
                {plan.status === "active" ? <ToggleRight size={14} className="text-green-500" /> : <ToggleLeft size={14} />}
                {plan.status === "active" ? "Disable" : "Enable"}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Subscribers tab */}
      {tab === "subscribers" && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {["Customer ID", "Plan ID", "Status", "Start Date", "Next Billing", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {subs.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">No subscribers yet</td></tr>
                ) : subs.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-700">User #{s.user_id}</td>
                    <td className="px-4 py-3 text-gray-600">Plan #{s.plan_id}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_CHIP[s.status] || ""}`}>{s.status}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{s.start_date ? new Date(s.start_date).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{s.next_billing_date ? new Date(s.next_billing_date).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        {s.status === "active" && (
                          <button onClick={() => handleSubStatus(s.id, "paused")}
                            className="px-2 py-1 text-xs border rounded-lg text-yellow-700 hover:bg-yellow-50">Pause</button>
                        )}
                        {s.status === "paused" && (
                          <button onClick={() => handleSubStatus(s.id, "active")}
                            className="px-2 py-1 text-xs border rounded-lg text-green-700 hover:bg-green-50">Resume</button>
                        )}
                        {s.status !== "cancelled" && (
                          <button onClick={() => handleSubStatus(s.id, "cancelled")}
                            className="px-2 py-1 text-xs border rounded-lg text-red-600 hover:bg-red-50">Cancel</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Plan Drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing ? "Edit Plan" : "Create Plan"}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name <span className="text-red-500">*</span></label>
            <input value={form.name} onChange={F("name")} placeholder="e.g. Family Weekly Plan" className={inp} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={F("description")} rows={2} placeholder="What's included..."
              className={`${inp} resize-none`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price ($) <span className="text-red-500">*</span></label>
              <input type="number" min="0" step="0.01" value={form.price} onChange={F("price")} placeholder="0.00" className={inp} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meals per Cycle</label>
              <input type="number" min="1" value={form.meals_per_cycle} onChange={F("meals_per_cycle")} className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle</label>
              <select value={form.billing_cycle} onChange={F("billing_cycle")} className={sel}>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={F("status")} className={sel}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <button onClick={handleSave} disabled={saving}
            className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition disabled:opacity-60">
            {saving ? "Saving..." : editing ? "Update Plan" : "Create Plan"}
          </button>
        </div>
      </Drawer>
    </div>
  );
};

export default SubscriptionsAdminPage;
